/**
 * Everything said over a fresh grave. Toasts — ≤ 120 chars at maximal context,
 * and every line repeats the item's name back: the undo button next to it has
 * to be about something.
 *
 * The name goes after a colon or into a slot where its own grammatical case
 * cannot be wrong. Lithuanian will not let a verb agree with a noun it has
 * never met, and the dry register already shipped „Varškės sūrelis pašalinta"
 * — a masculine noun with a feminine participle — because a line assumed it
 * could inflect around an unknown word. It cannot. Colon, or nothing.
 */

import type { SassContext } from '@gramas/shared'
import { itemName } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const DELETES: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'entry_deleted.base': [
    (c) => `Ištrinta be gedulo: ${itemName(c)}.`,
    (c) => `Nebėra: ${itemName(c)}. Tarsi nieko nebuvo.`,
    (c) => `Ištrinta: ${itemName(c)}. Istorija perrašyta švariai.`,
    (c) => `Iškeliavo: ${itemName(c)}. Atminimas liks pas mane.`,
    (c) => `Pašalinta: ${itemName(c)}. Liudytojų nėra. Beveik.`,
    (c) => `Ištrinta: ${itemName(c)}. Skanaus atminimo.`,
    (c) => `Dingo: ${itemName(c)}. Suvalgytos kalorijos, deja, ne.`,
    (c) => `Pašalinta: ${itemName(c)}. Mačiau. Tylėsiu.`,
    (c) => `Ištrinta: ${itemName(c)}. RIP.`,
    (c) => `Išbraukta: ${itemName(c)}. Sąžinė — tavo reikalas.`,
    (c) => `Na gerai. Ištrinta: ${itemName(c)}.`,
    (c) => `Iš įrašų pašalinta: ${itemName(c)}. Iš atminties — kaip pavyks.`,
    (c) => `Ištrinta: ${itemName(c)}. Diena palengvėjo. Popieriuje.`,
    (c) => `Vienu įrašu mažiau: ${itemName(c)}. Elegantiška.`,
    (c) => `Anuliuota: ${itemName(c)}. Teismo posėdis atidėtas.`,
    (c) => `Ištrinta: ${itemName(c)}. Kartais redaktorius laimi.`,
    (c) => `Pašalinta: ${itemName(c)}. Tyliai, be ceremonijų.`,
    (c) => `Nurašyta: ${itemName(c)}. Aš užsimerkiau.`,
    (c) => `Ištrinta: ${itemName(c)}. Nulis dramos. Šįkart.`,
    (c) => `Išimta iš apyvartos: ${itemName(c)}.`,
    (c) => `Aha. Nebėra: ${itemName(c)}. Vietoje liko šiltas prisiminimas.`,
    (c) => `Ištrinta: ${itemName(c)}. Ir aš nieko neklausiu.`,
    (c) => `Dingo iš sąrašo: ${itemName(c)}. Sąrašas tyli.`,
    (c) => `Ištrinta: ${itemName(c)}. Jei ką — aš buvau prieš.`,
    (c) => `Pašalinta: ${itemName(c)}. Liks spraga. Išgyvensim.`,
  ],
  'workout_deleted.base': [
    (c) => `Ištrinta: ${itemName(c)}. Prakaitas negrąžinamas.`,
    (c) => `Treniruotė dingo: ${itemName(c)}. Raumenys prisimins. Gal.`,
    (c) => `Pašalinta: ${itemName(c)}. Sofa šypsosi.`,
    (c) => `Ištrinta: ${itemName(c)}. Kas sudeginta, tas sudeginta.`,
    (c) => `Iš sąrašo: ${itemName(c)}. Kūnas savo žino.`,
    (c) => `Pašalinta: ${itemName(c)}. Sportas liko istorijoje.`,
    (c) => `Ištrinta: ${itemName(c)}. Biudžetas neteko premijos.`,
    (c) => `Anuliuota: ${itemName(c)}. Pats judesys — ne.`,
  ],
  'recipe_deleted.base': [
    (c) => `Receptas į archyvą: ${itemName(c)}.`,
    (c) => `Ištrinta: ${itemName(c)}. Virtuvė gedės neilgai.`,
    (c) => `Pašalinta: ${itemName(c)}. Meniu tapo lakoniškesnis.`,
    (c) => `Ištrinta: ${itemName(c)}. Kulinarinis palikimas susitraukė.`,
    (c) => `Nurašyta: ${itemName(c)}. Puodai tyli.`,
    (c) => `Pašalinta: ${itemName(c)}. Vienu skyriumi mažiau.`,
    (c) => `Ištrinta: ${itemName(c)}. Kažkada buvo skanu. Turbūt.`,
    (c) => `Iš repertuaro išbraukta: ${itemName(c)}.`,
  ],
}

export const DELETES_META: Readonly<Record<string, number>> = {
  'entry_deleted.base': 21,
  'workout_deleted.base': 4,
  'recipe_deleted.base': 2,
}
